import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type Anomalia,
  type CanalResumen,
  type Ejemplo,
  type EstadoServicio,
  type ResumenProceso,
  type SaludHttp,
  type Serie,
  type Servicio,
  type VersionReportada,
} from '../api';
import { CAUSAS } from '../causas';
import { etiquetaDeCanal } from '../canales';
import { fechaYHora } from '../fechas';
import {
  cifraCorta,
  duracionLegible,
  rellenarTramos,
  tasa,
  variacion,
} from '../serie';
import { antiguedadEnMinutos } from '../ventanas';
import { GraficoSerie } from './GraficoSerie';
import { Icono } from './Icono';
import { Mini } from './Mini';

/**
 * El resumen: la pantalla a la que se llega sin saber qué mirar.
 *
 * El resto del panel exige elegir de antemano un proceso, un canal, una
 * pestaña. Esto junta lo que casi siempre se quiere saber primero —¿está todo
 * bien?, ¿qué cambió y cuándo?, ¿qué es lo que más falla?— y lleva de ahí al
 * detalle. No calcula nada que no calcule ya otra pantalla: cada panel sale
 * de una consulta que existe, de modo que el resumen y el detalle no pueden
 * contradecirse.
 *
 * Cada consulta falla por separado. Un panel que no carga —el despliegue no
 * está disponible, el rol no ve servicios— se omite sin tumbar los demás.
 */

type Pestana = 'procesos' | 'servicios' | 'versiones' | 'analisis';

interface Props {
  ventana: number;
  canal: string;
  recarga: number;
  puedeVerServicios: boolean;
  onVentana: (minutos: number) => void;
  onVerProceso: (proceso: string) => void;
  onIrA: (pestana: Pestana) => void;
  onAbrirTraza: (traceId: string) => void;
  onError: (err: unknown) => void;
}

const RANGOS = [
  { etiqueta: '15 min', minutos: 15 },
  { etiqueta: '1 h', minutos: 60 },
  { etiqueta: '24 h', minutos: 60 * 24 },
  { etiqueta: '7 d', minutos: 60 * 24 * 7 },
  { etiqueta: '30 d', minutos: 60 * 24 * 30 },
] as const;

interface Datos {
  serie: Serie | null;
  procesos: ResumenProceso[] | null;
  previos: ResumenProceso[] | null;
  anomalias: Anomalia[] | null;
  canales: CanalResumen[] | null;
  ultimo: string | null | undefined;
  recientes: Array<Ejemplo & { process_name: string }> | null;
  servicios: Servicio[] | null;
  http: SaludHttp[] | null;
  despliegue: EstadoServicio[] | null;
  versiones: VersionReportada[] | null;
}

const VACIO: Datos = {
  serie: null,
  procesos: null,
  previos: null,
  anomalias: null,
  canales: null,
  ultimo: undefined,
  recientes: null,
  servicios: null,
  http: null,
  despliegue: null,
  versiones: null,
};

const ORDEN_SEVERIDAD = { alta: 0, media: 1, baja: 2 } as const;

function sumar(filas: ResumenProceso[] | null) {
  const t = { ejecuciones: 0, ok: 0, fallas: 0, negocio: 0, abandonados: 0 };
  for (const f of filas ?? []) {
    t.ejecuciones += Number(f.ejecuciones);
    t.ok += Number(f.ok);
    t.fallas += Number(f.con_error);
    t.negocio += Number(f.con_negocio);
    t.abandonados += Number(f.abandonados);
  }
  return t;
}

/** «hace 4 s», «hace 12 min», «hace 3 h», «hace 2 días». */
function haceCuanto(ultimo: string): string {
  const minutos = antiguedadEnMinutos(ultimo);
  if (minutos < 1) return 'hace instantes';
  if (minutos < 60) return `hace ${minutos} min`;
  if (minutos < 60 * 24) return `hace ${Math.round(minutos / 60)} h`;
  return `hace ${Math.round(minutos / 60 / 24)} días`;
}

function porcentaje(valor: number): string {
  return `${valor.toLocaleString('es-CL', { maximumFractionDigits: valor < 10 ? 1 : 0 })}%`;
}

interface Indicador {
  id: string;
  titulo: string;
  valor: string;
  detalle: string;
  tono?: 'bien' | 'mal' | 'aviso';
  /** Cambio respecto de la ventana anterior, ya formateado. */
  cambio?: { texto: string; bueno: boolean | null };
  serie?: number[];
  color: string;
}

function cambioRelativo(actual: number, previo: number, buenoSiSube: boolean | null) {
  const v = variacion(actual, previo);
  if (v === null || Math.abs(v) < 0.005) return undefined;
  return {
    texto: `${v > 0 ? '↑' : '↓'} ${Math.abs(Math.round(v * 100)).toLocaleString('es-CL')}%`,
    bueno: buenoSiSube === null ? null : v > 0 === buenoSiSube,
  };
}

function cambioEnPuntos(actual: number, previo: number, buenoSiSube: boolean | null) {
  const d = actual - previo;
  if (Math.abs(d) < 0.05) return undefined;
  return {
    texto: `${d > 0 ? '↑' : '↓'} ${Math.abs(d).toLocaleString('es-CL', { maximumFractionDigits: 1 })} pp`,
    bueno: buenoSiSube === null ? null : d > 0 === buenoSiSube,
  };
}

export function Resumen({
  ventana,
  canal,
  recarga,
  puedeVerServicios,
  onVentana,
  onVerProceso,
  onIrA,
  onAbrirTraza,
  onError,
}: Props) {
  const [datos, setDatos] = useState<Datos>(VACIO);
  const [cargando, setCargando] = useState(true);
  const [ventanaDeDatos, setVentanaDeDatos] = useState({ desdeMs: 0, hastaMs: 0 });

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    const hastaMs = Date.now();
    const desdeMs = hastaMs - ventana * 60_000;
    const desde = new Date(desdeMs).toISOString();
    const hasta = new Date(hastaMs).toISOString();
    const previoDesde = new Date(desdeMs - ventana * 60_000).toISOString();
    const c = canal || undefined;

    void Promise.allSettled([
      api.serie(desde, hasta, c),
      api.resumen(desde, hasta, c),
      api.resumen(previoDesde, desde, c),
      api.anomalias(),
      api.canales(desde),
      api.ultimo(),
      api.recientes(desde, c),
      puedeVerServicios ? api.servicios(desde, c) : Promise.resolve(null),
      api.despliegue(),
      api.versionesReportadas(),
    ]).then((r) => {
      if (!vigente) return;
      const ok = <T,>(i: number): T | null => {
        const x = r[i];
        return x && x.status === 'fulfilled' ? (x.value as T) : null;
      };
      // Los dos primeros son lo que el resumen existe para mostrar: si
      // fallan, se dice. Los demás son complementos y se omiten en silencio.
      for (const i of [0, 1]) {
        const x = r[i];
        if (x && x.status === 'rejected') onError(x.reason);
      }
      const servicios = ok<{ servicios: Servicio[]; http?: SaludHttp[] }>(7);
      setDatos({
        serie: ok<Serie>(0),
        procesos: ok<{ procesos: ResumenProceso[] }>(1)?.procesos ?? null,
        previos: ok<{ procesos: ResumenProceso[] }>(2)?.procesos ?? null,
        anomalias: ok<{ anomalias: Anomalia[] }>(3)?.anomalias ?? null,
        canales: ok<{ comparativa: CanalResumen[] }>(4)?.comparativa ?? null,
        ultimo: ok<{ ultimo: string | null }>(5)?.ultimo,
        recientes: ok<{ ejecuciones: Array<Ejemplo & { process_name: string }> }>(6)?.ejecuciones ?? null,
        servicios: servicios?.servicios ?? null,
        http: servicios?.http ?? null,
        despliegue: ok<{ servicios: EstadoServicio[] }>(8)?.servicios ?? null,
        versiones: ok<{ versiones: VersionReportada[] }>(9)?.versiones ?? null,
      });
      setVentanaDeDatos({ desdeMs, hastaMs });
      setCargando(false);
    });

    return () => {
      vigente = false;
    };
  }, [ventana, canal, recarga, puedeVerServicios, onError]);

  const tramos = useMemo(
    () =>
      datos.serie
        ? rellenarTramos(datos.serie.puntos, ventanaDeDatos.desdeMs, ventanaDeDatos.hastaMs, datos.serie.paso_segundos)
        : [],
    [datos.serie, ventanaDeDatos]
  );

  const actual = sumar(datos.procesos);
  const previo = sumar(datos.previos);
  const hayPrevio = previo.ejecuciones > 0;

  const alertas = [...(datos.anomalias ?? [])].sort(
    (a, b) => ORDEN_SEVERIDAD[a.severidad] - ORDEN_SEVERIDAD[b.severidad]
  );
  const hayGraves = alertas.some((a) => a.severidad === 'alta');

  const indicadores: Indicador[] = [
    {
      id: 'ejecuciones',
      titulo: 'Ejecuciones',
      valor: cifraCorta(actual.ejecuciones),
      detalle: `${datos.procesos?.length ?? 0} procesos`,
      ...(hayPrevio ? { cambio: cambioRelativo(actual.ejecuciones, previo.ejecuciones, null) } : {}),
      serie: tramos.map((t) => t.ejecuciones),
      color: 'var(--serie-4)',
    },
    {
      id: 'exito',
      titulo: 'Tasa de éxito',
      valor: porcentaje(tasa(actual.ok, actual.ejecuciones)),
      detalle: `${cifraCorta(actual.ok)} completadas`,
      tono: 'bien',
      ...(hayPrevio
        ? { cambio: cambioEnPuntos(tasa(actual.ok, actual.ejecuciones), tasa(previo.ok, previo.ejecuciones), true) }
        : {}),
      serie: tramos.map((t) => tasa(t.ok, t.ejecuciones)),
      color: 'var(--bien)',
    },
    {
      id: 'fallas',
      titulo: 'Tasa de fallas',
      valor: porcentaje(tasa(actual.fallas, actual.ejecuciones)),
      detalle: `${cifraCorta(actual.fallas)} con falla`,
      tono: actual.fallas > 0 ? 'mal' : undefined,
      ...(hayPrevio
        ? { cambio: cambioEnPuntos(tasa(actual.fallas, actual.ejecuciones), tasa(previo.fallas, previo.ejecuciones), false) }
        : {}),
      serie: tramos.map((t) => tasa(t.fallas, t.ejecuciones)),
      color: 'var(--mal)',
    },
    {
      id: 'negocio',
      titulo: 'De negocio',
      valor: porcentaje(tasa(actual.negocio, actual.ejecuciones)),
      detalle: `${cifraCorta(actual.negocio)} rechazos`,
      serie: tramos.map((t) => t.negocio),
      color: 'var(--texto-medio)',
    },
    {
      id: 'abandono',
      titulo: 'Abandono',
      valor: porcentaje(tasa(actual.abandonados, actual.ejecuciones)),
      detalle: `${cifraCorta(actual.abandonados)} sin completar`,
      tono: actual.abandonados > 0 ? 'aviso' : undefined,
      ...(hayPrevio
        ? { cambio: cambioEnPuntos(tasa(actual.abandonados, actual.ejecuciones), tasa(previo.abandonados, previo.ejecuciones), false) }
        : {}),
      serie: tramos.map((t) => tasa(t.abandonados, t.ejecuciones)),
      color: 'var(--aviso)',
    },
    {
      id: 'alertas',
      titulo: 'Alertas activas',
      valor: String(alertas.length),
      detalle: hayGraves ? `${alertas.filter((a) => a.severidad === 'alta').length} graves` : alertas.length ? 'ninguna grave' : 'todo en calma',
      tono: hayGraves ? 'mal' : alertas.length ? 'aviso' : 'bien',
      color: 'var(--mal)',
    },
  ];

  const procesos = datos.procesos ?? [];
  const masFallas = procesos
    .filter((p) => Number(p.ejecuciones) >= 5 && Number(p.con_error) > 0)
    .sort((a, b) => tasa(Number(b.con_error), Number(b.ejecuciones)) - tasa(Number(a.con_error), Number(a.ejecuciones)))
    .slice(0, 4);
  const masRechazos = procesos
    .filter((p) => Number(p.ejecuciones) >= 5 && Number(p.con_negocio) > 0)
    .sort((a, b) => tasa(Number(b.con_negocio), Number(b.ejecuciones)) - tasa(Number(a.con_negocio), Number(a.ejecuciones)))
    .slice(0, 4);
  const rutasLentas = [...(datos.servicios ?? [])].sort((a, b) => b.p95_ms - a.p95_ms).slice(0, 4);
  const serviciosConFallas = [...(datos.http ?? [])]
    .filter((s) => s.tasa_servidor > 0)
    .sort((a, b) => b.tasa_servidor - a.tasa_servidor)
    .slice(0, 4);

  const estadoGlobal = hayGraves
    ? { texto: 'Con alertas graves', tono: 'mal' }
    : alertas.length
      ? { texto: 'Con avisos', tono: 'aviso' }
      : { texto: 'Operativo', tono: 'bien' };

  const versionesPorCanal = useMemo(() => {
    const mejor = new Map<string, VersionReportada>();
    for (const v of datos.versiones ?? []) {
      const previo = mejor.get(v.canal);
      if (!previo || v.visto > previo.visto) mejor.set(v.canal, v);
    }
    return [...mejor.values()].slice(0, 3);
  }, [datos.versiones]);

  const serviciosArriba = (datos.despliegue ?? []).filter((s) => s.ok).length;

  return (
    <div className="resumen">
      <div className="kpis">
        {indicadores.map((k) => (
          <div className={`kpi ${k.tono ?? ''}`} key={k.id}>
            <span className="kpi-titulo">{k.titulo}</span>
            <strong className="kpi-valor">{k.valor}</strong>
            <span className="kpi-detalle">
              {k.cambio && (
                <em className={`kpi-cambio ${k.cambio.bueno === null ? '' : k.cambio.bueno ? 'bueno' : 'malo'}`}>
                  {k.cambio.texto}
                </em>
              )}
              {k.detalle}
            </span>
            {k.serie && k.serie.length > 1 && <Mini valores={k.serie} color={k.color} />}
          </div>
        ))}
      </div>

      <div className="resumen-principal">
        <section className="panel estado" aria-label="Estado general">
          <header className="panel-cabecera">
            <h3>Estado</h3>
          </header>
          <div className={`estado-global ${estadoGlobal.tono}`}>
            <i aria-hidden="true" />
            {estadoGlobal.texto}
          </div>
          <dl className="datos">
            <div>
              <dt>Último dato</dt>
              <dd>{datos.ultimo ? haceCuanto(datos.ultimo) : datos.ultimo === null ? 'sin datos' : '—'}</dd>
            </div>
            {datos.despliegue && (
              <div>
                <dt>Servicios</dt>
                <dd className={serviciosArriba < datos.despliegue.length ? 'malo' : ''}>
                  {serviciosArriba} de {datos.despliegue.length}
                </dd>
              </div>
            )}
            {versionesPorCanal.map((v) => (
              <div key={v.canal}>
                <dt>{etiquetaDeCanal(v.canal)}</dt>
                <dd>{v.app || '—'}</dd>
              </div>
            ))}
          </dl>

          {datos.canales && datos.canales.length > 0 && (
            <>
              <h4 className="subtitulo">Canales</h4>
              <ul className="canales-estado">
                {datos.canales.map((c) => {
                  const exito = tasa(Number(c.ok), Number(c.ejecuciones));
                  const tono = exito >= 95 ? 'bien' : exito >= 85 ? 'aviso' : 'mal';
                  return (
                    <li key={c.canal}>
                      <span>
                        <i className={`punto ${tono}`} aria-hidden="true" />
                        {etiquetaDeCanal(c.canal)}
                      </span>
                      <b>{porcentaje(exito)}</b>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <button type="button" className="enlace-panel" onClick={() => onIrA('versiones')}>
            Ver versiones
          </button>
        </section>

        <section className="panel actividad" aria-label="Actividad en el tiempo">
          <header className="panel-cabecera">
            <h3>Actividad</h3>
            <div className="rangos" role="group" aria-label="Ventana de tiempo">
              {RANGOS.map((r) => (
                <button
                  key={r.minutos}
                  type="button"
                  className={ventana === r.minutos ? 'activo' : ''}
                  aria-pressed={ventana === r.minutos}
                  onClick={() => onVentana(r.minutos)}
                >
                  {r.etiqueta}
                </button>
              ))}
            </div>
          </header>
          <GraficoSerie
            tramos={tramos}
            pasoSegundos={datos.serie?.paso_segundos ?? 3600}
            ventanaDias={ventana / 1440}
            cargando={cargando}
          />
          {datos.serie?.fuente === 'diario' && (
            <p className="nota-panel">
              Más allá de 30 días solo se conocen los rechazos de negocio que el SDK cerró como tales.
            </p>
          )}
        </section>
      </div>

      <div className="tops">
        <Lista
          titulo="Procesos con más fallas"
          vacio="Ningún proceso con fallas en esta ventana."
          filas={masFallas.map((p) => ({
            clave: p.proceso,
            texto: p.proceso,
            apoyo: `${cifraCorta(Number(p.con_error))} de ${cifraCorta(Number(p.ejecuciones))}`,
            pastilla: porcentaje(tasa(Number(p.con_error), Number(p.ejecuciones))),
            tono: 'mal',
            alClic: () => onVerProceso(p.proceso),
          }))}
        />
        <Lista
          titulo="Con más rechazos de negocio"
          vacio="Sin rechazos de negocio."
          filas={masRechazos.map((p) => ({
            clave: p.proceso,
            texto: p.proceso,
            apoyo: `${cifraCorta(Number(p.con_negocio))} de ${cifraCorta(Number(p.ejecuciones))}`,
            pastilla: porcentaje(tasa(Number(p.con_negocio), Number(p.ejecuciones))),
            tono: 'neutro',
            alClic: () => onVerProceso(p.proceso),
          }))}
        />
        {puedeVerServicios && (
          <Lista
            titulo="Rutas más lentas"
            vacio="Sin llamadas registradas."
            filas={rutasLentas.map((s) => ({
              clave: `${s.servicio}:${s.ruta}:${s.canal ?? ''}`,
              texto: s.ruta,
              apoyo: s.servicio,
              pastilla: duracionLegible(s.p95_ms),
              tono: 'neutro',
              codigo: true,
              alClic: () => onIrA('servicios'),
            }))}
          />
        )}
        {puedeVerServicios && (
          <Lista
            titulo="Servicios con fallas"
            vacio="Sin fallas 5xx."
            filas={serviciosConFallas.map((s) => ({
              clave: `${s.servicio}:${s.ruta}:${s.metodo}`,
              texto: s.ruta,
              apoyo: `${s.metodo} · ${s.servicio}`,
              pastilla: `5xx ${s.tasa_servidor.toLocaleString('es-CL', { maximumFractionDigits: 1 })}%`,
              tono: 'mal',
              codigo: true,
              alClic: () => onIrA('servicios'),
            }))}
          />
        )}
      </div>

      <div className="inferior">
        <section className="panel" aria-label="Alertas activas">
          <header className="panel-cabecera">
            <h3>Alertas activas</h3>
            {puedeVerServicios && (
              <button type="button" className="enlace-panel" onClick={() => onIrA('analisis')}>
                Ver análisis
              </button>
            )}
          </header>
          {alertas.length === 0 ? (
            <p className="vacio-panel">Sin alertas activas.</p>
          ) : (
            <ul className="alertas-lista">
              {alertas.slice(0, 5).map((a) => (
                <li key={`${a.tipo}:${a.metrica}:${a.ambito}:${a.canal}`}>
                  <span className={`sev ${a.severidad}`}>{a.severidad}</span>
                  <div>
                    <strong>{a.ambito}</strong>
                    <span>{a.explicacion}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {datos.despliegue && (
          <section className="panel" aria-label="Estado de servicios">
            <header className="panel-cabecera">
              <h3>Estado de servicios</h3>
              <button type="button" className="enlace-panel" onClick={() => onIrA('versiones')}>
                Ver todos
              </button>
            </header>
            <ul className="servicios-estado">
              {datos.despliegue.map((s) => (
                <li key={s.servicio} className={s.ok ? 'ok' : 'caido'}>
                  <strong>{s.servicio}</strong>
                  <span className={`resultado ${s.ok ? 'ok' : 'error'}`}>{s.ok ? 'OK' : 'CAÍDO'}</span>
                  <small>{s.latenciaMs === null ? '—' : `${s.latenciaMs} ms`}</small>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="panel ancha" aria-label="Últimas ejecuciones con problema">
          <header className="panel-cabecera">
            <h3>Últimas ejecuciones con problema</h3>
          </header>
          {!datos.recientes || datos.recientes.length === 0 ? (
            <p className="vacio-panel">
              {datos.recientes ? 'Sin ejecuciones con problema en esta ventana.' : 'Sin detalle para esta ventana.'}
            </p>
          ) : (
            <div className="tabla-recientes" role="table" aria-label="Ejecuciones recientes con problema">
              {datos.recientes.map((e) => (
                <button
                  type="button"
                  className="reciente"
                  role="row"
                  key={e.trace_id + e.process_id}
                  onClick={() => (puedeVerServicios ? onAbrirTraza(e.trace_id) : onVerProceso(e.process_name))}
                  title={puedeVerServicios ? 'Abrir la traza' : 'Ver el proceso'}
                >
                  <span className="reciente-hora" role="cell">{fechaYHora(e.ts)}</span>
                  <span className="reciente-proceso" role="cell">{e.process_name}</span>
                  <span role="cell">
                    <span className={`resultado ${e.result.toLowerCase()}`}>{e.result === 'BUSINESS_ERROR' ? 'NEGOCIO' : e.result}</span>
                  </span>
                  <span className="reciente-causa" role="cell">
                    {e.causa ? (
                      <span className={`causa ${e.causa}`} title={CAUSAS[e.causa].explicacion}>
                        {CAUSAS[e.causa].etiqueta}
                      </span>
                    ) : null}
                  </span>
                  <span className="reciente-duracion" role="cell">{duracionLegible(e.duration_ms)}</span>
                  <Icono nombre="traza" tamano={16} />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

interface FilaLista {
  clave: string;
  texto: string;
  apoyo: string;
  pastilla: string;
  tono: 'mal' | 'neutro';
  codigo?: boolean;
  alClic: () => void;
}

function Lista({ titulo, filas, vacio }: { titulo: string; filas: FilaLista[]; vacio: string }) {
  return (
    <section className="panel lista" aria-label={titulo}>
      <header className="panel-cabecera">
        <h3>{titulo}</h3>
      </header>
      {filas.length === 0 ? (
        <p className="vacio-panel">{vacio}</p>
      ) : (
        <ul>
          {filas.map((f) => (
            <li key={f.clave}>
              <button type="button" onClick={f.alClic}>
                <span className="lista-texto">
                  {f.codigo ? <code title={f.texto}>{f.texto}</code> : <strong title={f.texto}>{f.texto}</strong>}
                  <small>{f.apoyo}</small>
                </span>
                <span className={`pastilla-valor ${f.tono}`}>{f.pastilla}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
